import {
  ActionBar,
  Alert,
  Button,
  Card,
  EmptyState,
  PageHeader,
  ResponsiveList,
  StatusBadge,
  Skeleton,
  useMediaQuery,
} from '@catering/ui';
import type { ActionBarAction } from '@catering/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link, getRouteApi, useNavigate } from '@tanstack/react-router';
import { Building2, Plus, Search } from 'lucide-react';
import type { JSX } from 'react';

import type { CustomerStatus, Zone } from '../../customers/api';
import { searchCustomers, ZONE_LABELS } from '../../customers/api';
import { formatTaxId } from '../../customers/format-tax-id';
import { GuideLink } from '../../guide/guide-link';
import { CustomerSearchBar } from './components/customer-search-bar';

const PAGE_SIZE = 20;

// `protectedRoute` in `router.tsx` is pathless (`id: 'protected'`), so a
// child declared with `path: '/customers'` still gets nested under it in
// the route id, even though its URL is the absolute `/customers`.
const customersRoute = getRouteApi('/protected/customers');

interface FilterPatch {
  q?: string;
  zone?: Zone | undefined;
  status?: CustomerStatus | undefined;
}

/**
 * `customer-search/spec.md` → "Pantalla de listado y búsqueda en
 * back-office". List state lives in the URL (`validateSearch` in
 * `router.tsx`) so reload/back keep it, per `design.md` §7.
 */
export function CustomerListPage(): JSX.Element {
  const search = customersRoute.useSearch();
  const navigate = customersRoute.useNavigate();
  const appNavigate = useNavigate();
  const isDesktop = useMediaQuery('(min-width: 640px)');

  const query = useQuery({
    queryKey: ['customers', search.q, search.zone, search.status, search.page],
    queryFn: () =>
      searchCustomers({
        q: search.q === '' ? undefined : search.q,
        zone: search.zone,
        status: search.status,
        page: search.page,
        pageSize: PAGE_SIZE,
      }),
    placeholderData: keepPreviousData,
  });

  function applyFilters(patch: FilterPatch): void {
    void navigate({ search: (prev) => ({ ...prev, ...patch, page: 1 }) });
  }

  function goToPage(page: number): void {
    void navigate({ search: (prev) => ({ ...prev, page }) });
  }

  const hasActiveFilters =
    search.q !== '' || search.zone !== undefined || search.status !== undefined;

  const headerActions: ActionBarAction[] = [
    { label: 'Nueva empresa', icon: Plus, onClick: () => void appNavigate({ to: '/customers/new' }) },
  ];
  const primaryAction = headerActions[0];
  const secondaryActions = headerActions.slice(1);

  const totalPages =
    query.data === undefined ? 1 : Math.max(1, Math.ceil(query.data.total / PAGE_SIZE));

  return (
    <section className="flex flex-col gap-6">
      <PageHeader
        icon={Building2}
        title="Clientes"
        description="Gestioná las empresas cliente registradas en el sistema."
        actions={
          isDesktop ? (
            <div className="flex items-center gap-2">
              <Link to="/customers/new">
                <Button icon={Plus}>Nueva empresa</Button>
              </Link>
              <GuideLink moduleId="customers" />
            </div>
          ) : (
            <div className="flex items-center gap-2">
              {primaryAction === undefined ? null : (
                <ActionBar primary={primaryAction} secondary={secondaryActions} />
              )}
              <GuideLink moduleId="customers" />
            </div>
          )
        }
      />

      <Card>
        <CustomerSearchBar
          query={search.q}
          zone={search.zone}
          status={search.status}
          onQueryChange={(q) => applyFilters({ q })}
          onZoneChange={(zone) => applyFilters({ zone })}
          onStatusChange={(status) => applyFilters({ status })}
        />
      </Card>

      {query.isError ? (
        <Alert tone="danger" title="No pudimos cargar las empresas">
          <div className="flex items-center justify-between gap-4">
            <span>Ocurrió un error al buscar. Intentá de nuevo.</span>
            <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>
              Reintentar
            </Button>
          </div>
        </Alert>
      ) : null}

      {query.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : null}

      {query.data !== undefined && query.data.items.length === 0 ? (
        <EmptyState
          icon={hasActiveFilters ? Search : Building2}
          title={
            hasActiveFilters
              ? 'No se encontraron empresas.'
              : 'Todavía no hay empresas registradas.'
          }
        />
      ) : null}

      {query.data !== undefined && query.data.items.length > 0 ? (
        <>
          <ResponsiveList
            items={query.data.items}
            rowKey={(item) => item.id}
            caption="Empresas cliente"
            getRowTestId={(item) => `customer-card-${item.id}`}
            emptyState={null}
            columns={[
              {
                key: 'legalName',
                header: 'Razón social',
                render: (item) => (
                  <Link
                    to="/customers/$customerId"
                    params={{ customerId: item.id }}
                    className="font-medium text-slate-900 hover:underline"
                  >
                    {item.legalName}
                  </Link>
                ),
              },
              { key: 'tradeName', header: 'Nombre comercial', render: (item) => item.tradeName },
              { key: 'taxId', header: 'CUIT', render: (item) => formatTaxId(item.taxId) },
              { key: 'zone', header: 'Zona', render: (item) => ZONE_LABELS[item.zone] },
              {
                key: 'status',
                header: 'Estado',
                render: (item) => <StatusBadge domain="customer" status={item.status} />,
              },
            ]}
            renderCardHeader={(item) => (
              <Link
                to="/customers/$customerId"
                params={{ customerId: item.id }}
                className="flex items-center justify-between gap-2"
              >
                <span>{item.legalName}</span>
                <StatusBadge domain="customer" status={item.status} />
              </Link>
            )}
            renderCardBody={(item) => (
              <Link
                to="/customers/$customerId"
                params={{ customerId: item.id }}
                className="col-span-2 grid grid-cols-2 gap-2"
              >
                <span>{item.tradeName}</span>
                <span>{formatTaxId(item.taxId)}</span>
                <span>{ZONE_LABELS[item.zone]}</span>
              </Link>
            )}
          />

          <div className="flex items-center justify-between text-sm text-slate-600">
            <span>
              {query.data.total} {query.data.total === 1 ? 'empresa' : 'empresas'} en total
            </span>

            <div className="flex items-center gap-3">
              <Button
                variant="secondary"
                size="sm"
                disabled={search.page <= 1}
                onClick={() => goToPage(search.page - 1)}
              >
                Anterior
              </Button>
              <span>
                Página {search.page} de {totalPages}
              </span>
              <Button
                variant="secondary"
                size="sm"
                disabled={search.page >= totalPages}
                onClick={() => goToPage(search.page + 1)}
              >
                Siguiente
              </Button>
            </div>
          </div>
        </>
      ) : null}
    </section>
  );
}
